import React, { useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Formik, Form, Field } from 'formik'
import { FormattedMessage } from 'react-intl'
import { useSnackbar } from 'notistack'
import { IconButton, Button, ButtonGroup } from './Buttons'
import Modal from './Modal'
import FormGroup from './FormGroup'
import { createListView } from '../../actions/listViewActions'
import { getAggregateParam } from '../../selectors/collectionSelectors'
import { ICON_ADD } from '../../constants/icons'
import CheckBoxField from '../_form/CheckboxField'
import TextField from '../_form/TextField'
import RoleSelector from '../../selectors/hasRoleSelectors'

const initialValues = { name: '', public: false }

const SaveListViewAction = ({ collection }) => {
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const canSavePublicListViews = useSelector(RoleSelector.canSavePublicListViews)
  const isAggregate = useSelector((state) => getAggregateParam(state, collection)) || false

  // Read grid state from sessionStorage (StandardDataGrid format)
  // Don't use useMemo here - we need fresh state when the save button is clicked
  const getGridState = useCallback(() => {
    try {
      const storageKey = `${collection}GridState`
      const stored = sessionStorage.getItem(storageKey)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      // Silently fail
    }
    return null
  }, [collection])

  // Read column visibility from localStorage
  const getColumnVisibility = useCallback(() => {
    try {
      const storageKey = `${collection}ColumnVisibility`
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      // Silently fail
    }
    return null
  }, [collection])

  // Check if there are any changes to save (read fresh state on each render)
  const gridState = getGridState()
  const columnVisibility = getColumnVisibility()

  const hasFilters = gridState?.filterModel?.items?.length > 0
  const hasSorting = gridState?.sortModel?.length > 0
  const hasColumnChanges = columnVisibility !== null

  const hasChanges = hasFilters || hasSorting || hasColumnChanges

  const onSave = async (values) => {
    // Only use StandardDataGrid format (v2) for employees collection
    // Other collections continue using old ExpressionBuilder format
    const useStandardDataGridFormat = collection === 'employees'

    let searchQuery

    if (useStandardDataGridFormat) {
      // Read fresh state when saving (not cached)
      const currentGridState = getGridState()
      const currentColumnVisibility = getColumnVisibility()

      // Build the search query in StandardDataGrid format (v2)
      searchQuery = {
        version: 2, // Version marker to distinguish from old format
        aggregate: isAggregate,
        filterModel: currentGridState?.filterModel || { items: [], linkOperator: 'and' },
        sortModel: currentGridState?.sortModel || [],
        paginationModel: currentGridState?.paginationModel || { page: 0, pageSize: 25 },
        columnVisibilityModel: currentColumnVisibility || {}
      }
    } else {
      // For other collections, this component won't be used with StandardDataGrid
      // so gridState won't exist - this is just a fallback
      searchQuery = {
        aggregate: isAggregate
      }
    }

    await dispatch(
      createListView({
        name: values.name,
        searchQuery: JSON.stringify(searchQuery),
        screenType: collection.toUpperCase(),
        isPublic: values.public,
      })
    )

    enqueueSnackbar(<FormattedMessage id="toast.save_successful" />, { variant: 'success' })
    setShowModal(false)
  }

  if (!hasChanges) {
    return null
  }

  return (
    <>
      <IconButton iconName={ICON_ADD} type="button" color="lightgray" disabled={showModal} onClick={() => setShowModal(true)}>
        <FormattedMessage id="button.save" />
      </IconButton>

      {showModal && (
        <Modal header={<FormattedMessage id="title.save_list_view" />} onClose={() => setShowModal(false)}>
          <Formik initialValues={initialValues} validate={(values) => (!values.name ? { name: 'message.required' } : {})} onSubmit={onSave}>
            {() => (
              <Form>
                <FormGroup>
                  <Field name="name" component={TextField} type="text" label="label.name" placeholder="placeholder.name" required />
                </FormGroup>
                {canSavePublicListViews && (
                  <FormGroup>
                    <Field name="public" component={CheckBoxField} label="label.public" />
                  </FormGroup>
                )}
                <ButtonGroup>
                  <Button type="submit">
                    <FormattedMessage id="button.confirm" />
                  </Button>
                  <Button type="button" color="lightgray" onClick={() => setShowModal(false)}>
                    <FormattedMessage id="button.cancel" />
                  </Button>
                </ButtonGroup>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
    </>
  )
}

export default SaveListViewAction
